import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BookingNotification } from '@models/notifications/booking-notification.model';
import { ConsentFormNotification } from '@models/notifications/consent-form-notification.model';
import { DemographicNotification } from '@models/notifications/demographic-request-notification.model';
import { DocumentNotification } from '@models/notifications/document-notification.model';
import { GlobalNotificationModel } from '@models/notifications/global-notification.model';
import { MedicalHistoryNotification } from '@models/notifications/medical-history-request-notification.model';
import { GlobalNotification, GlobalNotificationData, Notification, NotificationData } from '@models/notifications/notification.model';
import { PaymentNotification } from '@models/notifications/payment-notification.model';
import { QuestionnaireNotification } from '@models/notifications/questionnaire-notification.model';
import { TelemedNotification } from '@models/notifications/telemed-notification.model';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of, ReplaySubject, Subject } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { AlertService } from './alert.service';
import { AuthService } from './auth.service';
import { WebSocketService } from './websocket.service';

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  notifications$ = new ReplaySubject<Notification[]>(1);
  amountOfUnreadNotifications$ = new ReplaySubject<number>(1);
  refresh$: Subject<void>;

  constructor(
    private readonly alert: AlertService,
    private readonly auth: AuthService,
    private readonly http: HttpClient,
    private readonly router: Router,
    private readonly websocket: WebSocketService,
    private readonly translateService: TranslateService,
  ) {
    this.refresh$ = new Subject<void>();

    this.auth.authenticated$
      .pipe(
        take(1),
        switchMap((authenticated) => {
          if (authenticated) {
            // We only get the amount of unread notifications here, not the notifications themselves.
            // Getting the notifications is done by calling the getNotifications method.
            return this.checkUnreadNotifications$();
          }
          return of(0);
        }),
      )
      .subscribe((amount) => {
        this.amountOfUnreadNotifications$.next(amount);
      });

    this.websocket
      .waitUntilReady()
      .pipe(switchMap(() => this.websocket.listen$('notifications')))
      .subscribe(() => {
        this.refresh$.next();
      });
  }

  // Notifications are fetched from the database as is.
  // In most screens a more informative description, icon or date is required, or even some extra properties
  // derived from state and/or other properties on the notification.
  // For this reason there is the mapping function, declared on the NotificationService to keep relevance.
  // So if you do not want the raw data where you call the getNotifications method, just map it on the screen
  // using the mapping function.
  private frontendMapping(n: NotificationData): Notification {
    n.icon = Notification.types[n.type].icon;
    // Only some notifs have mapped classes.
    switch (n.type) {
      case 'telemed': {
        return new TelemedNotification(n, this.translateService);
      }
      case 'questionnaire': {
        return new QuestionnaireNotification(n, this.translateService);
      }
      case 'consent-form': {
        return new ConsentFormNotification(n, this.translateService);
      }
      case 'document': {
        return new DocumentNotification(n, this.translateService);
      }
      case 'global-notification': {
        return new GlobalNotificationModel(n, this.translateService);
      }
      case 'payment': {
        return new PaymentNotification(n, this.translateService);
      }
      case 'booking': {
        return new BookingNotification(n, this.translateService);
      }
      case 'demographic-request': {
        return new DemographicNotification(n, this.translateService);
      }
      case 'medical-history': {
        return new MedicalHistoryNotification(n, this.translateService);
      }
      default:
        return new Notification(n);
    }
  }
  private checkUnreadNotifications$(): Observable<number> {
    return this.http.get<{ unread_notifications: number }>('/api/notifications/unread').pipe(
      catchError(() => of([] as Notification[])),
      map((obj: { unread_notifications: number }) => obj.unread_notifications),
    );
  }

  fetchNotifications$(state = 'unread'): Observable<Notification[]> {
    return this.http.get<NotificationData[]>('/api/notifications', { params: { state } }).pipe(
      catchError((err: unknown) => {
        this.alert.handleErrorDialog$(err, this.translateService.instant('error_fetching_notifications'));
        return of([] as Notification[]);
      }),
      map((notifications: NotificationData[]) => notifications.map((notification) => this.frontendMapping(notification))),
    );
  }

  markAsRead$(notification: Notification): Observable<void> {
    return this.http.patch<{ result: boolean }>(`/api/notifications/${notification.id}`, { read: true }).pipe(
      map((obj: { result: boolean }) => {
        if (obj.result) {
          this.checkUnreadNotifications$().subscribe((amount) => {
            this.amountOfUnreadNotifications$.next(amount);
          });
        }
      }),
    );
  }

  /**
   * This is what takes you to the notification action, like opening a payment or opening a telemed room
   */
  notificationAction(notification: Notification) {
    this.markAsRead$(notification).pipe(take(1)).subscribe();
    const path: string = Notification.types[notification.type].path;

    const params = Object.entries(Notification.types[notification.type].params || []).map(([, value]) => notification[value.toString()]);

    const extractedQueryParams = Object.entries(Notification.types[notification.type].queryParams || {});
    const queryParams = Object.assign(
      {},
      ...extractedQueryParams.map(([property, value]) => ({ [property]: notification[value.toString()] })),
    );
    this.router.navigate([path, ...params], { queryParams });
  }

  markAllAsRead$(): Observable<any> {
    return this.http.patch('/api/notifications/mark-as-read', {});
  }

  getGlobalNotifications$(): Observable<GlobalNotification[]> {
    return this.http
      .get<{ notifications: GlobalNotificationData[] }>('/api/notifications/global')
      .pipe(map((res) => res.notifications.map((n) => new GlobalNotification(n))));
  }

  getGlobalLatestNotification$(): Observable<Notification | null> {
    return this.http.get<{ notifications: NotificationData[] }>('/api/notifications/global/latest').pipe(
      map((res) => {
        const n = res.notifications[0];
        return n ? this.frontendMapping(n) : null;
      }),
    );
  }
}
